// AuthProvider + useAuth.
//
// Wraps the app once at the root; downstream code reads from `useAuth()`
// rather than instantiating useAuthFlow per consumer.
//
// Also wires the API client's onUnauthorized callback into the flow's
// session-expired trigger so a 401 from any authed request kicks the user
// into the re-auth modal.

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { setOnUnauthorized } from '../api/client.js';
import { useAuthFlow, type AuthFlowApi } from './useAuthFlow.js';

const AuthContext = createContext<AuthFlowApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const api = useAuthFlow();

  useEffect(() => {
    setOnUnauthorized(api.flagSessionExpired);
    return () => setOnUnauthorized(null);
  }, [api.flagSessionExpired]);

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthFlowApi {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be called inside <AuthProvider>');
  }
  return ctx;
}
